/**
 * SplitCard — two or more panels that read as one card with an internal seam.
 *
 * The product builds combined tiles without drawing a single line: the panels
 * each carry the card fill, a narrow flex gap separates them, and the outer
 * container clips to the card radius. The gap lets the page canvas show
 * through, and that strip of background *is* the divider.
 *
 * Why it matters: a rule drawn with `border` sits on top of the fill and
 * reads as decoration. A seam is an absence — it says "these were cut from
 * one piece", which is precisely the relationship between a description and
 * its count, or a score and its movement.
 *
 * Children are expected to be `SplitPanel`s.
 *
 * @format
 */

import {type ReactNode} from 'react';

import {Box} from '@mui/material';
import {styled, type CSSObject} from '@mui/material/styles';

import {spacingPx, type SpacingTokenName} from 'ui/components/layout/spacing';
import {MotionDurationBase, MotionEasingStandard} from 'ui/tokens/ts/motion';
import {RadiusM} from 'ui/tokens/ts/radii';

import type {RowReflowAt} from 'ui/components/layout/Row';

import {CARD_FILL, CARD_FILL_HOVER} from '../helpers';

export interface SplitCardProps {
  /**
   * Направление ряда. `column` — когда шов нужен между строками списка, а
   * не между колонками: та же склейка, только по вертикали.
   */
  direction?: 'row' | 'column';
  /** Viewport width below which the panels stack vertically. */
  reflowAt?: Exclude<RowReflowAt, 'never'> | 'never';
  /** Fixed height so a row of SplitCards aligns. */
  height?: number;
  className?: string;
  children?: ReactNode;
}

const REFLOW_PX: Record<Exclude<RowReflowAt, 'never'>, number> = {sm: 480, md: 768, lg: 1024};

const Root = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{
  $reflowAt: Exclude<RowReflowAt, 'never'> | 'never';
  $height?: number;
  $direction: 'row' | 'column';
}>(({$reflowAt, $height, $direction}) => ({
  display: 'flex',
  flexDirection: $direction,
  alignItems: 'stretch',
  // The seam: page canvas showing between two filled panels.
  gap: spacingPx('xxs'),
  borderRadius: RadiusM,
  overflow: 'clip',
  backgroundColor: 'transparent',
  minWidth: 0,
  flex: '1 1 0',
  ...($height ? {height: $height} : null),
  ...($reflowAt === 'never'
    ? null
    : {
        [`@media (max-width: ${REFLOW_PX[$reflowAt]}px)`]: {
          flexDirection: 'column',
          height: 'auto',
        },
      }),
}));

export function SplitCard({
  direction = 'row',
  reflowAt = 'md',
  height,
  className,
  children,
}: SplitCardProps) {
  return (
    <Root $reflowAt={reflowAt} $height={height} $direction={direction} className={className}>
      {children}
    </Root>
  );
}

export interface SplitPanelProps {
  /** `grow` takes the remaining width; `hug` sizes to its content. */
  width?: 'grow' | 'hug';
  /** Centres content vertically — used by the numeric panel. */
  centered?: boolean;
  /** Внутренний отступ. `l` (32) — для панелей, несущих крупный контент. */
  padding?: Extract<SpacingTokenName, 'm' | 'l'>;
  /**
   * Делает панель кликабельной целиком.
   *
   * Строка списка, у которой цель шириной в карточку, промахнуться не даёт,
   * а отдельная кнопка «Открыть» в каждой строке — столько же одинаковых
   * кнопок на экране. Корпус при этом становится `button`: панель без
   * обработчика остаётся `div`, чтобы в разметке не заводилась кнопка,
   * которая никуда не ведёт.
   */
  onClick?: () => void;
  /** Доступное имя кнопки. Имеет смысл только вместе с `onClick`. */
  ariaLabel?: string;
  className?: string;
  children?: ReactNode;
}

interface PanelGeometry {
  $grow: boolean;
  $centered: boolean;
  $padding: SpacingTokenName;
}

function panelStyles({$grow, $centered, $padding}: PanelGeometry): CSSObject {
  return {
    backgroundColor: CARD_FILL,
    padding: spacingPx($padding),
    display: 'flex',
    flexDirection: 'column',
    gap: spacingPx('m'),
    justifyContent: $centered ? 'center' : 'flex-start',
    ...($grow ? {flex: '1 1 0', minWidth: 0} : {flex: '0 0 auto'}),
  };
}

const Panel = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<PanelGeometry>(props => panelStyles(props));

/**
 * Кликабельный корпус — отдельным компонентом, а не пропом `as` на `Panel`:
 * `styled(Box)` типизирован по своей `div`-сигнатуре, и `type`/`aria-*`
 * кнопки на нём не проходят по типам. Геометрия при этом общая — расходиться
 * в отступах корпусам нельзя.
 */
const InteractivePanel = styled('button', {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<PanelGeometry>(({theme, ...props}) => ({
  ...panelStyles(props),
  font: 'inherit',
  color: 'inherit',
  textAlign: 'left',
  width: '100%',
  boxSizing: 'border-box',
  border: 'none',
  // Внешние углы режет сам `SplitCard`; собственное скругление кнопки от
  // браузера торчало бы из-под этой обрезки.
  borderRadius: 0,
  cursor: 'pointer',
  transition: `background-color ${MotionDurationBase} ${MotionEasingStandard}`,
  '&:hover': {backgroundColor: CARD_FILL_HOVER},
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    // Рамка рисуется внутрь: `SplitCard` обрезает содержимое по радиусу, и
    // вынесенный наружу контур срезало бы вместе с углами.
    outlineOffset: -2,
  },
}));

export function SplitPanel({
  width = 'grow',
  centered = false,
  padding = 'm',
  onClick,
  ariaLabel,
  className,
  children,
}: SplitPanelProps) {
  const geometry: PanelGeometry = {
    $grow: width === 'grow',
    $centered: centered,
    $padding: padding,
  };

  if (onClick == null) {
    return (
      <Panel {...geometry} className={className}>
        {children}
      </Panel>
    );
  }

  return (
    <InteractivePanel
      {...geometry}
      type='button'
      onClick={onClick}
      aria-label={ariaLabel}
      className={className}
    >
      {children}
    </InteractivePanel>
  );
}
